import * as React from 'react';
import { Editor } from 'slate';
import { useSlate } from 'slate-react';
import styled from 'styled-components';

import { TextButton } from '@xing-com/button';
import { mediaSmallOrTiny } from '@xing-com/layout-tokens';
import {
  spaceS,
  xdlColorHover,
  xdlColorSelected,
  xdlColorTextInvert,
} from '@xing-com/tokens';

export const ToolbarButtonComponent = styled(TextButton)<{
  isHighlighted?: boolean;
  isDisabled: boolean;
  icon: React.ReactSVG;
}>`
  border-radius: 50px;
  margin-right: ${spaceS};
  background: ${(props) =>
    props.isHighlighted ? `${xdlColorSelected}` : 'none'};
  color: ${(props) => props.isHighlighted === true && `${xdlColorTextInvert}`};

  &:hover,
  &:active {
    color: ${(props) =>
      props.isHighlighted === true && `${xdlColorTextInvert}`};
    background: ${(props) => {
      if (props.isDisabled) {
        return 'none';
      }
      if (props.isHighlighted) {
        return `${xdlColorSelected}`;
      }
      return `${xdlColorHover}`;
    }};
  }

  &:last-child {
    margin-right: 0;
  }

  svg {
    height: 24px;
    width: 24px;
  }

  @media only screen and (min-width: 540px) {
    [class*='IconContainer'] {
      height: 24px;
      width: 24px;
    }
  }

  @media ${mediaSmallOrTiny} {
    &:last-of-type {
      margin-right: ${spaceS};
    }
  }

  @media (max-width: 389px) {
    margin: 0;
  }
`;

type ToolbarButtonProps = {
  button: {
    icon: React.ReactSVG;
    label?: string;
    isHighlighted: (editor: Editor) => boolean;
    isDisabled: (editor: Editor) => boolean;
    onClick: (event: React.MouseEvent, editor: Editor) => void;
    'data-testid'?: string;
    component?: React.FC;
    id?: string;
  };
};

export const ToolbarButton = ({ button }: ToolbarButtonProps) => {
  const editor = useSlate();
  const isHighlighted = button.isHighlighted(editor);
  const isDisabled = editor.selection === null || button.isDisabled(editor);
  const { label, icon, component, id } = button;

  const onButtonClick = (event: React.MouseEvent) => {
    event.preventDefault();
    if (!isDisabled) button.onClick(event, editor);
  };

  if (component) {
    const Component = component;
    return React.cloneElement(<Component />, {
      icon: button ? icon : null,
      isDisabled,
      isHighlighted,
      label,
      id,
    });
  }

  return (
    <ToolbarButtonComponent
      aria-label={label}
      data-testid={button['data-testid']}
      disabled={isDisabled}
      // @ts-expect-error ???
      icon={icon}
      id={id}
      isHighlighted={isHighlighted}
      onMouseDown={(event: React.MouseEvent) => onButtonClick(event)}
      onPointerDown={(event: React.PointerEvent) => onButtonClick(event)}
      size="medium"
      text={!isHighlighted}
    >
      {button && button.icon ? null : button.label}
    </ToolbarButtonComponent>
  );
};
